import { deepClone } from 'valtio/utils'

type AnyStore = Record<string, unknown>

/**
 * Reset a valtio store with a FIXED shape to its initial state.
 *
 * Assigns every key of `initialState` onto the store, so the store must
 * declare all of its keys up front (e.g. `{ status, error, loaded }`).
 *
 * @remarks
 *   Not for dictionary stores keyed by dynamic ids: `Object.keys({})` is empty,
 *   so nothing would be reset. Use {@link clearStore} for those.
 *
 * @param store The store to reset
 * @param initialState The state to reset the store to
 *
 * @see https://valtio.dev/docs/how-tos/how-to-reset-state
 */
export function resetStore<T extends AnyStore>(store: T, initialState: T) {
  const resetObj = deepClone(initialState)
  Object.keys(resetObj).forEach((key) => {
    store[key as keyof T] = resetObj[key as keyof T]
  })
}

/**
 * Empty a valtio store shaped as a DICTIONARY (`Record<id, Entity>`), where
 * the set of keys is dynamic and the initial state is `{}`.
 *
 * Deletes each current key in place. Deletion is a tracked op in valtio, so
 * subscribers are notified, and the proxy identity stays stable so existing
 * `useSnapshot(store)` consumers keep working.
 *
 * For stores with a fixed shape use {@link resetStore}.
 *
 * @param store The dictionary store to empty
 */
export function clearStore<T extends AnyStore>(store: T) {
  for (const key of Object.keys(store)) {
    delete store[key]
  }
}
